// Types for per-program daily activity timetables (camp schedules).
// Backed by the `program_schedules` table. Generic against `programs` so any
// program type can carry a schedule; summer camps are the first consumer.
//
// The schedule is a calendar model: each day owns an independent, time-ordered
// list of events. Days can have completely different times and a different
// number of events — there is no shared time grid across days.

/** One timed event within a single day. */
export interface ScheduleEvent {
  /** Start time, "HH:MM" 24h, e.g. "09:30". "" for an untimed event. */
  start: string;
  /** End time, "HH:MM" 24h, e.g. "10:00". Equals `start` for a point event. */
  end: string;
  /** Bilingual activity text, e.g. "Check in 签到". */
  activity: string;
}

/** One day column with its own independent list of events. */
export interface DaySchedule {
  /** Column header, e.g. "Monday 周一". */
  label: string;
  /** This day's events, time-ordered (sorted by `start`). */
  events: ScheduleEvent[];
}

/** The JSONB `schedule` payload: a list of day columns. */
export interface ScheduleGridData {
  days: DaySchedule[];
}

/** A full program schedule row as stored in `program_schedules`. */
export interface ProgramSchedule {
  id: string;
  program_id: string;
  theme_en: string | null;
  theme_zh: string | null;
  notes: string | null;
  schedule: ScheduleGridData;
  created_at: string;
  updated_at: string;
}

const DEFAULT_DAY_LABELS = [
  'Monday 周一',
  'Tuesday 周二',
  'Wednesday 周三',
  'Thursday 周四',
  'Friday 周五',
];

/** Empty grid used when authoring a brand-new schedule (5 weekdays, no events). */
export const EMPTY_SCHEDULE_GRID: ScheduleGridData = {
  days: DEFAULT_DAY_LABELS.map(label => ({ label, events: [] })),
};

/** Pad a 1–2 digit hour/minute to 2 digits. */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Parse a single clock token ("8:30", "8:30 AM", "14:00", "2 PM") into "HH:MM"
 * 24h form. Returns "" when the token can't be parsed.
 *
 * When no AM/PM marker is present and the hour is 1–6 (and not already 24h),
 * it is inferred as PM — a camp/after-school-day heuristic so legacy labels like
 * "1:00 - 2:00" correctly mean the afternoon, not 1 AM. Hours 7–12 stay as-is
 * (7–11 = morning, 12 = noon).
 */
function parseClock(token: string): string {
  const t = token.trim();
  if (!t) return '';
  const m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])?$/);
  if (!m) return '';
  let hour = parseInt(m[1], 10);
  const minute = m[2] ? parseInt(m[2], 10) : 0;
  if (hour > 23 || minute > 59) return '';
  const ampm = m[3]?.toLowerCase();
  if (ampm === 'am') {
    if (hour === 12) hour = 0;
  } else if (ampm === 'pm') {
    if (hour !== 12) hour += 12;
  } else if (hour >= 1 && hour <= 6) {
    // No AM/PM given: assume afternoon for a school-day schedule.
    hour += 12;
  }
  if (hour > 23) return '';
  return `${pad2(hour)}:${pad2(minute)}`;
}

/**
 * Parse a legacy time-slot label ("9:30 - 10:00", "8:30 AM", "All day") into a
 * `{ start, end }` pair of "HH:MM" strings. A single token sets `end = start`;
 * an unparseable label yields `{ start: '', end: '' }`.
 */
export function parseTimeRange(label: string): { start: string; end: string } {
  const raw = String(label ?? '').trim();
  if (!raw) return { start: '', end: '' };
  // Split on hyphen, en dash, em dash, or "to".
  const parts = raw.split(/\s*(?:-|–|—|to)\s*/i).filter(Boolean);
  const start = parseClock(parts[0] ?? '');
  const end = parts.length > 1 ? parseClock(parts[1] ?? '') : start;
  return { start, end: end || start };
}

/**
 * Format an "HH:MM" 24h time for 12-hour display without an AM/PM suffix, e.g.
 * "08:30" → "8:30", "13:00" → "1:00", "00:00" → "12:00". Non-HH:MM passes through.
 * Ordering on the calendar's time axis disambiguates morning vs. afternoon.
 */
export function formatTime(hhmm: string): string {
  const m = String(hhmm ?? '').match(/^(\d{2}):(\d{2})$/);
  if (!m) return String(hhmm ?? '');
  const hour = parseInt(m[1], 10);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${m[2]}`;
}

/** Sort comparator for events by start time; untimed ("") events sort first. */
function byStart(a: ScheduleEvent, b: ScheduleEvent): number {
  if (a.start === b.start) return 0;
  if (!a.start) return -1;
  if (!b.start) return 1;
  return a.start < b.start ? -1 : 1;
}

function coerceEvent(value: unknown): ScheduleEvent {
  const e = (value ?? {}) as Partial<ScheduleEvent>;
  return {
    start: String(e.start ?? ''),
    end: String(e.end ?? e.start ?? ''),
    activity: e.activity == null ? '' : String(e.activity),
  };
}

/** A legacy matrix row: shared time label + one cell per day column. */
interface LegacyRow {
  time?: unknown;
  cells?: unknown;
}

/**
 * Convert a legacy matrix payload `{ days: string[], rows: [{time, cells[]}] }`
 * into the calendar shape. Empty cells are dropped (no forced blank events).
 */
function migrateLegacy(days: string[], rows: LegacyRow[]): ScheduleGridData {
  const out: DaySchedule[] = days.map(label => ({ label: String(label), events: [] }));
  for (const row of rows) {
    const { start, end } = parseTimeRange(String((row?.time ?? '')));
    const cells = Array.isArray(row?.cells) ? row.cells : [];
    out.forEach((day, i) => {
      const activity = cells[i] == null ? '' : String(cells[i]);
      if (activity.trim()) day.events.push({ start, end, activity });
    });
  }
  for (const day of out) day.events.sort(byStart);
  return { days: out };
}

/**
 * Normalize an unknown JSONB value into a well-formed ScheduleGridData.
 * Tolerates the new calendar shape, the legacy matrix shape, and empty payloads.
 */
export function normalizeScheduleGrid(value: unknown): ScheduleGridData {
  const obj = (value ?? {}) as { days?: unknown; rows?: unknown };
  const rawDays = Array.isArray(obj.days) ? obj.days : [];
  // New shape carries object-valued day columns ({ label, events }); legacy days
  // are plain strings. Detect the new shape first so a stray `rows` (e.g. an
  // empty array sent only to satisfy an older API validator) is ignored.
  const daysAreObjects = rawDays.some(d => d != null && typeof d === 'object');

  if (!daysAreObjects && Array.isArray(obj.rows)) {
    return migrateLegacy(rawDays.map(d => String(d)), obj.rows as LegacyRow[]);
  }

  // New calendar shape.
  return {
    days: rawDays.map(d => {
      const day = (d ?? {}) as Partial<DaySchedule>;
      const events = Array.isArray(day.events) ? day.events.map(coerceEvent) : [];
      events.sort(byStart);
      return { label: String(day.label ?? ''), events };
    }),
  };
}
